import { T, useT } from '@transifex/react';
import { VStack } from '@vidahealth/vida-ui';
import { useState } from 'react';

import { useRulesEngine } from '@/modules/rulesEngine';
import { AlertInfoCard, CtaButton, Screen, ScreenHeading, TextField } from '@/modules/ui';

export function InviteCodeForm() {
  const t = useT();
  const {
    draft,
    setDraft,
    validateAndUseInviteCode,
    eligibilityCheckResult,
    setEligibilityCheckResult,
  } = useRulesEngine();

  // Local-only state: empty-field validation. isChecking + passed live in
  // eligibilityCheckResult so the form doesn't track its own submit / error.
  const [showRequiredError, setShowRequiredError] = useState(false);

  const onChangeText = (text: string) => {
    setDraft((prev) => ({ ...prev, inviteCode: text }));
    setShowRequiredError(false);
    setEligibilityCheckResult(undefined);
  };

  const onContinue = async () => {
    const inviteCode = draft.inviteCode?.trim();

    if (!inviteCode) {
      setShowRequiredError(true);
      return;
    }

    validateAndUseInviteCode();
  };

  const isChecking = eligibilityCheckResult?.isChecking === true;
  const showInvalidError = eligibilityCheckResult?.passed === false;
  const errorMessage = showRequiredError
    ? t('Required')
    : showInvalidError
      ? t('This is an invalid code')
      : undefined;

  return (
    <Screen
      footer={
        <CtaButton
          isBusy={isChecking}
          isDisabled={isChecking}
          onPress={onContinue}
          testID='invite-code-continue'
        >
          <T _str='Continue' />
        </CtaButton>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('Please enter your Vida invite code')}
          subtitle={t(
            "We'll use this information to verify your identity and get you set up with Vida"
          )}
        />

        <TextField
          value={draft.inviteCode ?? ''}
          onChangeText={onChangeText}
          placeholder={t('Invite Code')}
          autoCapitalize='none'
          autoCorrect={false}
          errorMessage={errorMessage}
          testID='invite-code'
        />

        <AlertInfoCard
          title={t('Where can I find this?')}
          description={t(
            'Your Vida invite code should be found in your invite or enrollment email you received from your company or health insurance.'
          )}
        />
      </VStack>
    </Screen>
  );
}
